import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Copy, ExternalLink, Save } from "lucide-react";

export default function BookingLinksTab() {
  const { currentOrgId, currentOrg } = useAuth();
  const [types, setTypes] = useState([]);
  const [profile, setProfile] = useState(null);
  const orgSlug = currentOrg?.slug;

  useEffect(() => {
    if (!currentOrgId) return;
    apiFetch(`/scheduling/appointment-types?organization_id=${currentOrgId}`).then((r) => setTypes(r.appointment_types || []));
    apiFetch(`/scheduling/user-profile?organization_id=${currentOrgId}`).then(setProfile).catch(() => {});
  }, [currentOrgId]);

  const origin = window.location.origin;
  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("Link copied"); };

  const saveProfile = async () => {
    try {
      await apiFetch("/scheduling/user-profile", {
        method: "PUT",
        body: JSON.stringify({ organization_id: currentOrgId, slug: profile.slug,
          default_zoom_link: profile.default_zoom_link || null, bio: profile.bio || "",
          display_name: profile.display_name || "" }),
      });
      toast.success("Profile saved");
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <p className="text-sm text-[#57534E]">Share these links with clients. All bookings sync back to ClientDesk VA.</p>

      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-5 space-y-4">
        <div>
          <h2 className="font-display font-semibold text-base">Organization booking page</h2>
          <p className="text-xs text-[#57534E]">Shows all public appointment types.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input readOnly value={`${origin}/book/${orgSlug || ""}`} className="flex-1"
                 data-testid="booklink-org" />
          <Button variant="outline" size="sm" onClick={() => copy(`${origin}/book/${orgSlug}`)}
                  data-testid="booklink-org-copy" className="rounded-full">
            <Copy size={12} className="mr-1" /> Copy
          </Button>
          <a href={`${origin}/book/${orgSlug}`} target="_blank" rel="noreferrer"
             className="p-2 rounded-full hover:bg-[#F5F5F4]" data-testid="booklink-org-open">
            <ExternalLink size={14} />
          </a>
        </div>
      </div>

      {profile && (
        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-5 space-y-4">
          <div>
            <h2 className="font-display font-semibold text-base">My personal booking link</h2>
            <p className="text-xs text-[#57534E]">Send clients directly to your calendar.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>URL slug</Label>
              <Input value={profile.slug || ""}
                     onChange={(e) => setProfile({...profile, slug: e.target.value})}
                     className="mt-1.5" data-testid="booklink-user-slug" />
            </div>
            <div>
              <Label>Default Zoom link (for Zoom appointments)</Label>
              <Input value={profile.default_zoom_link || ""}
                     onChange={(e) => setProfile({...profile, default_zoom_link: e.target.value})}
                     placeholder="https://zoom.us/j/..." className="mt-1.5" data-testid="booklink-zoom" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input readOnly value={`${origin}/book/${orgSlug || ""}/staff/${profile.slug || ""}`} className="flex-1"
                   data-testid="booklink-user" />
            <Button variant="outline" size="sm" onClick={() => copy(`${origin}/book/${orgSlug}/staff/${profile.slug}`)}
                    data-testid="booklink-user-copy" className="rounded-full">
              <Copy size={12} className="mr-1" /> Copy
            </Button>
            <Button onClick={saveProfile} data-testid="booklink-save-profile"
                    className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">
              <Save size={12} className="mr-1" /> Save
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-5 space-y-3">
        <h2 className="font-display font-semibold text-base">Per-appointment-type links</h2>
        {types.length === 0 && <p className="text-xs text-[#A8A29E]">No appointment types yet.</p>}
        <div className="space-y-2">
          {types.map((t) => {
            const url = `${origin}/book/${orgSlug}/${t.slug}`;
            return (
              <div key={t.id} className="flex items-center gap-2 flex-wrap"
                   data-testid={`booklink-type-${t.id}`}>
                <div className="min-w-[180px]">
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-xs text-[#A8A29E]">{t.duration_minutes} min · {t.visibility}</div>
                </div>
                <Input readOnly value={url} className="flex-1 min-w-[220px]" />
                <Button variant="outline" size="sm" onClick={() => copy(url)}
                        className="rounded-full" data-testid={`booklink-type-copy-${t.id}`}>
                  <Copy size={12} className="mr-1" /> Copy
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
